// src/features/stock-info/components/FinancialTabs.tsx
//
// Component Island: the financial deep-dive. Dynamic tabs (Key Metrics /
// Ratios / Growth / Estimates), a period selector (Annual / Quarterly / TTM),
// a statement selector for Growth, an interactive trend bar-chart, and a data
// table — all rendered GENERICALLY from whatever keys the endpoint returns.
//
// Key casing differs per endpoint (key-metrics is snake_case; ratios/growth/
// estimates are raw FMP camelCase), so columns + the chartable-metric list are
// derived at runtime and headers are humanised on the fly. id-like columns
// (symbol/date/period/fiscalYear/reportedCurrency/calendarYear) are hidden.

import { useEffect, useMemo, useState } from 'react';
import {
    Bar,
    BarChart,
    Cell,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    fetchEstimates,
    fetchGrowth,
    fetchKeyMetrics,
    fetchRatios,
    type FinancialRow,
} from '../api';

type Tab = 'metrics' | 'ratios' | 'growth' | 'estimates';
type Period = 'annual' | 'quarter' | 'ttm';

const TABS: Tab[] = ['metrics', 'ratios', 'growth', 'estimates'];

// Columns that identify a row rather than describe a value — never charted,
// never shown as a metric column.
const EXCLUDED_KEYS = new Set<string>([
    'date',
    'symbol',
    'period',
    'fiscalYear',
    'reportedCurrency',
    'calendarYear',
]);

// TTM is only meaningful for point-in-time snapshots (metrics/ratios). Growth
// and estimates are period-series, so they only support annual/quarter.
const TTM_CAPABLE: Record<Tab, boolean> = {
    metrics: true,
    ratios: true,
    growth: false,
    estimates: false,
};

const formatHeader = (key: string): string =>
    key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (str) => str.toUpperCase());

const formatValue = (val: string | number | null | undefined): string => {
    if (typeof val === 'number') {
        return new Intl.NumberFormat('en-US', {
            notation: 'compact',
            maximumFractionDigits: 2,
        }).format(val);
    }
    return val ?? '-';
};

const getBarColor = (value: number): string => {
    if (value > 0) return '#10b981';
    if (value < 0) return '#ef4444';
    return '#f97316';
};

export const FinancialTabs = ({ symbol }: { symbol: string }) => {
    const [activeTab, setActiveTab] = useState<Tab>('metrics');
    const [period, setPeriod] = useState<Period>('annual');
    const [statement, setStatement] = useState<string>('income-statement');
    const [data, setData] = useState<FinancialRow[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [chartMetric, setChartMetric] = useState<string>('');

    // Growth & Estimates don't support TTM — snap back to annual if the user
    // switches into one of those tabs while TTM is selected.
    useEffect(() => {
        if (!TTM_CAPABLE[activeTab] && period === 'ttm') {
            setPeriod('annual');
        }
    }, [activeTab, period]);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);

        const loadData = async () => {
            try {
                let res: FinancialRow[] = [];
                if (activeTab === 'metrics') {
                    res = await fetchKeyMetrics(symbol, period);
                } else if (activeTab === 'ratios') {
                    res = await fetchRatios(symbol, period);
                } else if (activeTab === 'growth') {
                    res = await fetchGrowth(symbol, statement, period);
                } else if (activeTab === 'estimates') {
                    res = await fetchEstimates(symbol, period);
                }

                if (cancelled) return;

                const rows = res ?? [];
                setData(rows);

                // Auto-select the first numeric field for the trend chart.
                if (rows.length > 0) {
                    const numericKeys = Object.keys(rows[0]).filter(
                        (k) => !EXCLUDED_KEYS.has(k) && typeof rows[0][k] === 'number',
                    );
                    setChartMetric((prev) =>
                        prev && numericKeys.includes(prev)
                            ? prev
                            : numericKeys[0] ?? '',
                    );
                } else {
                    setChartMetric('');
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(err);
                if (!cancelled) {
                    setData([]);
                    setChartMetric('');
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        void loadData();
        return () => {
            cancelled = true;
        };
    }, [symbol, activeTab, period, statement]);

    // Reverse for the trend chart so it reads left-to-right chronologically.
    const chartData = useMemo(() => [...data].reverse(), [data]);

    // All non-id columns present on the current dataset.
    const availableMetrics = useMemo(() => {
        if (data.length === 0) return [];
        return Object.keys(data[0]).filter((k) => !EXCLUDED_KEYS.has(k));
    }, [data]);

    // Only numeric columns can be charted.
    const numericMetrics = useMemo(() => {
        if (data.length === 0) return [];
        return availableMetrics.filter((k) => typeof data[0][k] === 'number');
    }, [availableMetrics, data]);

    // First handful of columns for the table so it fits on screen.
    const tableColumns = availableMetrics.slice(0, 6);

    return (
        <div className="surface-card border-t-4 border-emerald-500">
            {/* Header controls */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 border-b border-slate-200 dark:border-slate-800 pb-4">
                <div className="flex gap-4 flex-wrap w-full md:w-auto">
                    {TABS.map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`pb-1 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 ${
                                activeTab === tab
                                    ? 'border-emerald-600 text-emerald-600 dark:text-emerald-400'
                                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 border-transparent'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                <div className="flex items-center gap-3">
                    {activeTab === 'growth' && (
                        <select
                            value={statement}
                            onChange={(e) => setStatement(e.target.value)}
                            className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm font-medium rounded-lg px-3 py-1.5 text-slate-700 dark:text-slate-300 outline-none focus:border-emerald-500"
                        >
                            <option value="income-statement">Income Statement</option>
                            <option value="balance-sheet-statement">Balance Sheet</option>
                            <option value="cash-flow-statement">Cash Flow</option>
                        </select>
                    )}
                    <select
                        value={period}
                        onChange={(e) => setPeriod(e.target.value as Period)}
                        className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm font-medium rounded-lg px-3 py-1.5 text-slate-700 dark:text-slate-300 outline-none focus:border-emerald-500"
                    >
                        <option value="annual">Annual</option>
                        <option value="quarter">Quarterly</option>
                        {TTM_CAPABLE[activeTab] && (
                            <option value="ttm">Trailing 12M (TTM)</option>
                        )}
                    </select>
                </div>
            </div>

            <div className="relative min-h-[400px]">
                {isLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
                        <span className="text-slate-500 dark:text-slate-400 font-medium animate-pulse">
                            Loading {activeTab}…
                        </span>
                    </div>
                )}

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                    {/* Left: interactive trend chart */}
                    <div className="flex flex-col h-[350px]">
                        <div className="flex justify-between items-center mb-4 gap-3">
                            <h3 className="font-semibold text-slate-700 dark:text-slate-300">
                                Trend Analysis
                            </h3>
                            <select
                                value={chartMetric}
                                onChange={(e) => setChartMetric(e.target.value)}
                                className="bg-slate-50 dark:bg-slate-800 text-xs border border-slate-200 dark:border-slate-700 rounded px-2 py-1 outline-none max-w-[200px] truncate text-slate-700 dark:text-slate-300"
                            >
                                {numericMetrics.map((m) => (
                                    <option key={m} value={m}>
                                        {formatHeader(m)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex-1 w-full">
                            {data.length > 0 && chartMetric ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={chartData}
                                        margin={{ left: -10, top: 10, right: 0, bottom: 0 }}
                                    >
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            vertical={false}
                                            stroke="#334155"
                                            opacity={0.3}
                                        />
                                        <XAxis
                                            dataKey="date"
                                            tick={{ fontSize: 10, fill: '#64748b' }}
                                            axisLine={false}
                                        />
                                        <YAxis
                                            tickFormatter={(val: number) =>
                                                val.toLocaleString(undefined, {
                                                    notation: 'compact',
                                                })
                                            }
                                            tick={{ fontSize: 10, fill: '#64748b' }}
                                            axisLine={false}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#0f172a',
                                                borderColor: '#334155',
                                                borderRadius: '8px',
                                                color: '#fff',
                                            }}
                                            itemStyle={{
                                                color: '#10b981',
                                                fontWeight: 'bold',
                                            }}
                                            formatter={(value: number | string) => [
                                                typeof value === 'number'
                                                    ? new Intl.NumberFormat('en-US', {
                                                          notation: 'compact',
                                                          maximumFractionDigits: 3,
                                                      }).format(value)
                                                    : value,
                                                formatHeader(chartMetric),
                                            ]}
                                        />
                                        <Bar
                                            dataKey={chartMetric}
                                            radius={[4, 4, 0, 0]}
                                            isAnimationActive={false}
                                        >
                                            {chartData.map((entry, index) => {
                                                const value =
                                                    typeof entry[chartMetric] === 'number'
                                                        ? entry[chartMetric]
                                                        : 0;
                                                return (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={getBarColor(value)}
                                                    />
                                                );
                                            })}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center text-slate-500 dark:text-slate-400">
                                    No chart data
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right: data table */}
                    <div className="overflow-auto max-h-[350px] border border-slate-200 dark:border-slate-700 rounded-lg relative">
                        {data.length > 0 ? (
                            <table className="w-full text-sm text-left whitespace-nowrap">
                                <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 uppercase text-xs sticky top-0 z-10 shadow-sm">
                                    <tr>
                                        <th className="py-3 px-4 font-medium">Date</th>
                                        {tableColumns.map((m) => (
                                            <th
                                                key={m}
                                                className="py-3 px-4 font-medium text-right"
                                            >
                                                {formatHeader(m)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {data.map((row, i) => (
                                        <tr
                                            key={i}
                                            className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                                        >
                                            <td className="py-3 px-4 font-medium text-slate-900 dark:text-white">
                                                {row.date ?? 'TTM'}
                                            </td>
                                            {tableColumns.map((m) => (
                                                <td
                                                    key={m}
                                                    className="py-3 px-4 text-right text-slate-600 dark:text-slate-400 font-mono"
                                                >
                                                    {formatValue(row[m])}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="h-full flex items-center justify-center text-slate-500 dark:text-slate-400 py-10">
                                No tabular data available
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinancialTabs;
